<script setup lang="ts">
import PrimaryButton from '@/components/Buttons/PrimaryButton.vue'
import RadioButton from '@/components/Buttons/RadioButton.vue'
import EventCheckpointAutocomplete from '@/components/Filters/components/EventCheckpointAutocomplete.vue'
import StackLayout from '@/components/Layouts/StackLayout.vue'
import BaseDialog from '@/components/Modals/Dialog/BaseDialog.vue'
import TypographyItem from '@/components/Typography/TypographyItem.vue'
import checkpointApi from '@/services/checkpoint.service'
import {
    ERdrChannel,
    ERdrChannelLabels,
    hasAvailableRdrChannel,
    RdrChannelKey
} from '@/types/enum/RdrChannelEnum'
import type { CheckpointAssociateErrorModel, CheckpointModel } from '@/types/models/CheckpointModel'
import type { DeviceModel } from '@/types/models/DeviceModel'
import type { EventModel } from '@/types/models/EventModel'
import moment from 'moment'
import { computed, ref, watch } from 'vue'
import { DATE_FORMAT } from '../../../utils/dateUtils'
import CreateButton from '../../Buttons/CreateButton.vue'

type Props = {
    device?: Partial<DeviceModel>
}
const props = defineProps<Props>()
type Emits = {
    (e: 'refresh', device?: Partial<DeviceModel>): void
}
const emit = defineEmits<Emits>()

const event = ref(props.device?.event)
const checkpoint = ref<Partial<CheckpointModel>>()

const submitted = ref(false)
const isInEditMode = ref(false)
const isLoading = ref(false)

watch(
    () => props.device?.event,
    newEvent => (event.value = newEvent)
)

const associateError = ref<CheckpointAssociateErrorModel[]>()

const handleConfirm = (force?: boolean) => {
    submitted.value = true
    if (!props.device || !checkpoint.value?.id || !event.value?.id) return // TODO : Alerte
    isLoading.value = true
    return checkpointApi
        .associateDeviceToCheckpoint(
            event.value.id,
            checkpoint.value.id,
            props.device?.device_id ?? props.device.id!,
            selectedRdrChannel.value ? { [RdrChannelKey]: selectedRdrChannel.value } : undefined,
            force
        )
        .then(() => {
            emit('refresh', props.device)
            isInEditMode.value = false
            submitted.value = false
            event.value = props.device?.event
            open.value = false
            checkpoint.value = undefined
        })
        .catch(err => (associateError.value = err))
        .finally(() => {
            if (force) associateError.value = undefined
            isLoading.value = false
        })
}

const handleClose = () => {
    open.value = false
    associateError.value = undefined
}

const handleSelectEvent = (newEvent?: EventModel) => {
    checkpoint.value = undefined
    event.value = newEvent
}

const selectedRdrChannel = ref<ERdrChannel | undefined>()
const availableRdrChannels = computed(() =>
    props.device && checkpoint.value?.associates
        ? hasAvailableRdrChannel(props.device, checkpoint.value?.associates)
        : undefined
)

const open = ref(false)
</script>

<template>
    <PrimaryButton
        @click="open = true"
        :label="$t('device.actions.reassignCheckpoint')"
        v-bind="$attrs" />
    <BaseDialog
        :title="$t('device.actions.reassignCheckpoint')"
        :open="open"
        @confirm="handleConfirm"
        @close="handleClose"
        :disabled="!checkpoint || !!associateError"
        class="event-checkpoint-component modal-selector-half-width">
        <template #actions>
            <CreateButton
                v-if="associateError"
                :label="$t('checkpoint.buttons.forceAssociate')"
                @click="handleConfirm(true)" />
        </template>
        <template #content>
            <StackLayout :gap="8">
                <EventCheckpointAutocomplete
                    :event="event"
                    :checkpoint="checkpoint"
                    noQueryParam
                    :getCheckpointsParams="{
                        device_configuration: 1,
                        device_configuration_filter: [RdrChannelKey]
                    }"
                    @selectEvent="handleSelectEvent"
                    @selectCheckpoint="checkpoint = $event" />
            </StackLayout>
            <template v-if="availableRdrChannels && availableRdrChannels?.length">
                <StackLayout direction="column" :gap="8">
                    <TypographyItem :label="$t('device.actions.rdrChannels.rdrChannelsText')" />
                    <RadioButton
                        :modelValue="selectedRdrChannel"
                        @change="selectedRdrChannel = $event"
                        :options="
                            availableRdrChannels?.map(value => ({
                                name: $t(ERdrChannelLabels[value]?.label, { channel: value }),
                                value
                            }))
                        " />
                </StackLayout>
            </template>
            <template v-if="associateError && associateError?.length">
                <StackLayout direction="column" :gap="8">
                    <TypographyItem :label="$t('checkpoint.detail.alreadyAssociatedCheckpoints')" />
                    <TypographyItem
                        v-for="(item, index) in associateError"
                        :key="index"
                        :label="`${item.event} - ${item.checkpoint} - ${$t('date.from')} ${moment(
                            item.start_at
                        ).format(DATE_FORMAT)} ${$t('date.to')} ${moment(item.start_at).format(
                            DATE_FORMAT
                        )}`" />
                </StackLayout>
            </template>
        </template>
    </BaseDialog>
</template>

<style scoped lang="scss">
.event-checkpoint-component {
    & :deep(.dropdown-content) {
        position: fixed;
        display: flex;
        flex-flow: wrap;
    }
}
</style>
